import { useMemo, useRef } from 'react';
import { Grid } from '@mui/material';
import {
  useGetLossReasonAnalyticsQuery,
  useGetWinLossAnalyticsQuery,
} from 'store/api';
import { KpiCard } from '@centerpiece/components';
import { useAnalyticContext } from 'contexts/analytic.context';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Colors,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';

const backgroundColors = [
  'rgba(255, 99, 132, 0.9)',
  'rgba(54, 162, 235, 0.9)',
  'rgba(255, 206, 86, 0.9)',
  'rgba(75, 192, 192, 0.9)',
  'rgba(153, 102, 255, 0.9)',
  'rgba(255, 159, 64, 0.9)',
];
const borderColors = [
  'rgba(255, 99, 132, 1)',
  'rgba(54, 162, 235, 1)',
  'rgba(255, 206, 86, 1)',
  'rgba(75, 192, 192, 1)',
  'rgba(153, 102, 255, 1)',
  'rgba(255, 159, 64, 1)',
];

const borderWidth = 1;

ChartJS.register(ArcElement, Tooltip, Legend, Colors, ChartDataLabels);

export const WinRateKpiCards = () => {
  const {
    contactRecord,
    team,
    setTeam,
    individual,
    setIndividual,
    projectType,
    setProjectType,
  } = useAnalyticContext();
  const { data: kpiState = [], isFetching: kpiCardsLoading } =
    useGetWinLossAnalyticsQuery({
      params: {
        team: team?.id,
        individual: individual?.id,
        project_type: projectType.id,
      },
    });
  const { data: lost = [], isFetching: lostFetching } =
    useGetLossReasonAnalyticsQuery({
      params: {
        team: team?.id,
        individual: individual?.id,
        project_type: projectType.id,
      },
    });
  const lostData = useMemo(() => {
    const data: any = {};
    let labels: string[] = [];
    let dataPoints: number[] = [];
    let totalProjects: number = 0;
    let backgroundColor: string[] = [];
    let borderColor: string[] = [];
    lost?.forEach((l: any, index: number) => {
      labels = [...labels, l.reason];
      dataPoints = [...dataPoints, l.num_projects];
      totalProjects += l.num_projects;
      backgroundColor = [...backgroundColor, backgroundColors[index % 5]];
      borderColor = [...borderColor, borderColors[index % 5]];
    });
    return {
      labels,
      datasets: [{ data: dataPoints, borderColor, backgroundColor }],
      borderWidth,
      totalProjects,
    };
  }, [lost]);

  const kpiCards = useMemo(() => {
    const won_rev = kpiState
      ?.filter(
        (k: any) =>
          (k.awarded && k.state === 'Active') || k.state === 'Complete'
      )
      ?.reduce((acc: number, b: any) => acc + b.revenue_sum || 0, 0);
    const won_num = kpiState
      ?.filter(
        (k: any) =>
          (k.awarded && k.state === 'Active') || k.state === 'Complete'
      )
      ?.reduce((acc: number, b: any) => acc + b.num_projects || 0, 0);
    const lost_rev = kpiState
      ?.filter((k: any) => k.state === 'Lost')
      ?.reduce((acc: number, b: any) => acc + b.revenue_sum || 0, 0);
    const lost_num = kpiState
      ?.filter((k: any) => k.state === 'Lost')
      ?.reduce((acc: number, b: any) => acc + b.num_projects || 0, 0);
    return [
      {
        id: 'won-projects',
        title: 'Won Projects',
        value: won_rev,
        num: won_num,
      },
      {
        id: 'lost-projects',
        title: 'Lost Projects',
        value: lost_rev,
        num: lost_num,
      },
      {
        id: 'win_rate',
        title: 'Win Rate (by projects)',
        value: won_num / (won_num + lost_num),
        num: `${won_num}/${won_num + lost_num}`,
        percent: true,
      },
      {
        id: 'real_rev',
        title: 'Win Rate (by dollars)',
        value: won_rev / (won_rev + lost_rev),
        num: `${won_num}/${won_num + lost_num}`,
        percent: true,
      },
    ];
  }, [kpiState]);

  return (
    <Grid
      container
      spacing={2}
      alignItems={'stretch'}
      justifyContent={'space-between'}
    >
      <Grid
        key={`kpi-card-chart`}
        item
        xs={6}
        md={8}
        // alignItems={'space-between'}
      >
        <Grid
          container
          spacing={2}
          alignItems={'stretch'}
          justifyContent={'space-between'}
          sx={{ height: '100%' }}
        >
          {kpiCards.map(kpiCard => (
            <Grid key={`kpi-card-${kpiCard.id}`} item xs={6} md={6}>
              <KpiCard
                title={kpiCard.title}
                value={kpiCard?.value || 0}
                loading={kpiCardsLoading}
                caption={`${kpiCard.num || 0} projects`}
                percent={kpiCard.percent}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid key={`kpi-card-chart`} item xs={6} md={4} paddingBottom={2}>
        <KpiCard
          title={'Lost Reason %'}
          value={0}
          chart={
            lostData ? (
              <Pie
                data={lostData}
                height={225}
                width={400}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'right' },
                    datalabels: {
                      formatter: (value, ctx) => {
                        let percentage =
                          ((value * 100) / lostData.totalProjects).toFixed(0) +
                          '%';
                        return percentage;
                      },
                      color: 'black',
                      font: {
                        weight: 'bold',
                        // You can also set the size, family, etc. here
                        size: 14,
                        // family: 'Arial',
                      },
                    },
                    tooltip: {
                      callbacks: {
                        label: function (tooltipItem: any) {
                          let label =
                            lostData.labels[tooltipItem.dataIndex] || '';
                          if (label) {
                            label += ': ';
                          }

                          const currentValue =
                            lostData.datasets[0].data[tooltipItem.dataIndex];
                          const percentage = (
                            (currentValue / lostData.totalProjects) *
                            100
                          ).toFixed(0);
                          label += percentage + '%';
                          return label;
                        },
                      },
                    },
                  },
                  layout: {
                    padding: 0,
                  },
                }}
              />
            ) : undefined
          }
          loading={kpiCardsLoading}
          // caption={`${lostData.totalProjects} projects`}
          // percent={kpiCard.percent}
        />
      </Grid>
    </Grid>
  );
};
